import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FlaskConical, MapPin, Star, Home, Building2, Check, Loader2, ChevronRight, ShieldCheck, CheckCircle2, BadgeCheck, Navigation } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import PageHeader from "@/components/PageHeader";
import LocationPickerDialog from "@/components/LocationPickerDialog";
import { labApi } from "@/lib/api";
import { useAuthStore } from "@/store/authStore";
import SavePreferredButton from "@/components/SavePreferredButton";
import { useLocationStore } from "@/store/locationStore";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

function LabCard({ lab, selected, onSelect, userId }) {
  return (
    <div
      className={`relative w-full rounded-3xl border p-4 transition-all ${selected ? "border-primary ring-2 ring-primary/20" : "border-border hover:shadow-md"}`}
      data-testid={`lab-option-${lab.id}`}
    >
      <div className="absolute top-3 right-3 z-10">
        <SavePreferredButton userId={userId} provider={{ id: lab.id, name: lab.displayName || lab.name, city: lab.city, state: lab.state, address: lab.address, logoUrl: lab.logoUrl }} category="lab" variant="icon" />
      </div>
      <button onClick={() => onSelect(lab)} className="w-full text-left" data-testid={`lab-select-${lab.id}`}>
        <div className="flex items-start gap-3 pr-8">
          <div className="h-11 w-11 rounded-2xl bg-accent flex items-center justify-center shrink-0">
            <FlaskConical className="h-5 w-5 text-primary" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <p className="font-semibold truncate">{lab.displayName || lab.name}</p>
              {lab.verified && (
                <span className="flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap bg-emerald-100 text-emerald-700">
                  <BadgeCheck className="h-3 w-3" /> Verified by Trata
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate">{lab.address || lab.city || "Diagnostic lab"}</p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              <span className="text-xs flex items-center gap-1"><Star className="h-3 w-3 fill-amber-400 text-amber-400" />{lab.rating || 4}</span>
              {lab.distance != null && <span className="text-xs text-muted-foreground">{lab.distance.toFixed(1)} km</span>}
              {lab.nablAccredited && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">NABL</span>}
              {lab.homeCollectionAvailable && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">Home collection</span>}
            </div>
          </div>
          {selected && <CheckCircle2 className="h-5 w-5 text-primary shrink-0" />}
        </div>
      </button>
    </div>
  );
}

export default function LabBooking() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const profile = useAuthStore((s) => s.customerProfile);
  const coords = useLocationStore((s) => s.coords);
  const locCity = useLocationStore((s) => s.city);
  const locLabel = useLocationStore((s) => s.label);
  const detectLocation = useLocationStore((s) => s.detect);

  const [step, setStep] = useState("lab"); // lab | tests | details | done
  const [loading, setLoading] = useState(true);
  const [labs, setLabs] = useState([]);
  const [filter, setFilter] = useState("nearest"); // nearest | rated | verified
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [selectedLab, setSelectedLab] = useState(null);
  const [testsLoading, setTestsLoading] = useState(false);
  const [rateCard, setRateCard] = useState([]);
  const [selectedTests, setSelectedTests] = useState({}); // testId -> test
  const [booking, setBooking] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [collectionType, setCollectionType] = useState("home_collection");
  const [scheduledDate, setScheduledDate] = useState("");
  const [timeSlot, setTimeSlot] = useState("");
  const [address, setAddress] = useState("");
  const [patientName, setPatientName] = useState("");
  const [patientPhone, setPatientPhone] = useState("");
  const [terms, setTerms] = useState(false);
  const [digitalConsent, setDigitalConsent] = useState(true);

  useEffect(() => {
    const b = profile?.basicInfo || {};
    setPatientName(b.fullName || user?.name || "");
    setPatientPhone(user?.phone || "");
  }, [profile, user]);

  const loadLabs = useCallback(async () => {
    if (!user?._id) return;
    setLoading(true);
    try {
      const lat = coords?.latitude;
      const lng = coords?.longitude;
      let merged = [];
      if (lat) {
        // Fetch registered (verified) labs + the merged nearby search, exactly like mobile.
        const [browseRes, nearbyRes] = await Promise.all([
          labApi.browse(lat, lng, 50, locCity).catch(() => null),
          labApi.nearby(lat, lng, 25, locCity).catch(() => null),
        ]);
        const reg = (browseRes?.data?.labs || []).map((l) => ({
          ...l,
          displayName: l.name,
          distance: l.distance_km ?? null,
          rating: l.rating,
          homeCollectionAvailable: l.offers_home_collection,
          homeCollectionFee: l.home_collection_fee,
          verified: true,
        }));
        const near = (nearbyRes?.data?.labs || []).map((l) => ({
          ...l,
          verified: l.source ? l.source !== "google" : false,
        }));
        const map = new Map();
        [...reg, ...near].forEach((l) => {
          const key = l.id || `${l.name}-${l.address || ""}`;
          const existing = map.get(key);
          if (!existing) {
            map.set(key, l);
          } else {
            // Same lab from both sources — merge, keeping verified + the richest fields.
            map.set(key, {
              ...existing,
              ...l,
              verified: existing.verified || l.verified,
              distance: l.distance ?? existing.distance,
              homeCollectionAvailable: l.homeCollectionAvailable ?? existing.homeCollectionAvailable,
            });
          }
        });
        merged = Array.from(map.values());
      }
      if (!merged.length) {
        const res = await labApi.aiRecommended(user._id, lat, lng);
        merged = (res.data?.labs || []).map((l) => ({ ...l, verified: l.source ? l.source !== "google" : true }));
      }
      setLabs(merged);
    } catch (e) {
      setLabs([]);
    } finally {
      setLoading(false);
    }
  }, [user?._id, coords, locCity]);

  useEffect(() => {
    if (!coords?.latitude) detectLocation();
  }, [coords?.latitude, detectLocation]);

  useEffect(() => {
    loadLabs();
  }, [loadLabs]);

  const pickLab = async (lab) => {
    setSelectedLab(lab);
    setStep("tests");
    setTestsLoading(true);
    setRateCard([]);
    try {
      const res = await labApi.details(lab.id);
      setRateCard(res.data?.rateCard || []);
    } catch (e) {
      setRateCard([]);
      toast.error("Couldn't load this lab's tests.");
    } finally {
      setTestsLoading(false);
    }
  };

  const toggleTest = (t) => {
    setSelectedTests((prev) => {
      const next = { ...prev };
      if (next[t.testId]) delete next[t.testId];
      else next[t.testId] = t;
      return next;
    });
  };

  const chosen = Object.values(selectedTests);
  const subtotal = chosen.reduce((s, t) => s + (t.discountedPrice || t.price || 0), 0);
  const homeFee = collectionType === "home_collection" ? (selectedLab?.homeCollectionFee || 0) : 0;
  const total = subtotal + homeFee;

  const submit = async () => {
    if (!terms) return toast.error("Please accept the terms to continue.");
    if (!scheduledDate) return toast.error("Please pick a collection date.");
    if (!timeSlot) return toast.error("Please pick a time slot.");
    if (collectionType === "home_collection" && !address.trim()) return toast.error("Please enter a collection address.");
    if (!patientName.trim()) return toast.error("Please enter the patient name.");
    setSubmitting(true);
    try {
      const res = await labApi.book({
        userId: user._id,
        labId: selectedLab.id,
        labSelectionMethod: coords?.latitude ? "labs_near_me" : "ai_recommended",
        collectionType,
        patientName: patientName.trim(),
        patientPhone: patientPhone || undefined,
        patientType: "self",
        collectionAddress: collectionType === "home_collection" ? address.trim() : undefined,
        collectionLatitude: coords?.latitude,
        collectionLongitude: coords?.longitude,
        scheduledDate,
        scheduledTimeSlot: timeSlot,
        tests: chosen.map((t) => ({ testId: t.testId, testName: t.testName, price: t.discountedPrice || t.price || 0 })),
        digitalReportConsent: digitalConsent,
        termsAccepted: terms,
      });
      setBooking(res.data?.booking || null);
      setStep("done");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);

  const sortFn = filter === "rated"
    ? (a, b) => (b.rating || 0) - (a.rating || 0)
    : (a, b) => ((a.distance ?? 999) - (b.distance ?? 999));
  const filtered = (filter === "verified" ? labs.filter((l) => l.verified) : labs).slice().sort(sortFn);
  const registeredLabs = filtered.filter((l) => l.verified);
  const otherLabs = filtered.filter((l) => !l.verified);
  const locText = locLabel || locCity || "your area";

  const FILTERS = [
    { id: "nearest", label: "Nearest" },
    { id: "rated", label: "Top Rated" },
    { id: "verified", label: "Verified" },
  ];

  return (
    <div className="space-y-6 max-w-2xl">
      <PageHeader title="Book a Lab Test" subtitle="Home collection or lab visit — reports synced to your records" icon={FlaskConical} color="#16A34A" />

      {/* Step: choose lab */}
      {step === "lab" && (
        <>
          {/* Location bar — switch location like mobile */}
          <div className="flex items-center justify-between rounded-2xl border border-border bg-card px-4 py-3">
            <div className="flex items-center gap-2 min-w-0">
              <MapPin className="h-4 w-4 text-primary shrink-0" />
              <p className="text-sm min-w-0 truncate">
                <span className="text-muted-foreground">Showing labs for </span>
                <span className="font-semibold">{locText}</span>
              </p>
            </div>
            <button className="text-sm font-semibold text-primary flex items-center gap-1 shrink-0" onClick={() => setShowLocationPicker(true)} data-testid="lab-change-location">
              <Navigation className="h-3.5 w-3.5" /> Change
            </button>
          </div>

          {/* Filter chips */}
          <div className="flex items-center gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                data-testid={`lab-filter-${f.id}`}
                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${filter === f.id ? "bg-primary text-primary-foreground border-primary" : "bg-card text-muted-foreground border-border hover:bg-accent/40"}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24 rounded-3xl" />)}</div>
          ) : filtered.length === 0 ? (
            <Card className="rounded-3xl p-10 text-center border-dashed">
              <FlaskConical className="h-12 w-12 text-muted-foreground mx-auto" />
              <p className="mt-4 font-medium">No labs found in {locText}</p>
              <p className="text-sm text-muted-foreground mt-1">Try changing the location or switching off the Verified filter.</p>
              <Button className="mt-5 rounded-2xl" onClick={loadLabs} data-testid="lab-retry">Retry</Button>
            </Card>
          ) : (
            <div className="space-y-5">
              {registeredLabs.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-bold tracking-wide text-emerald-700 flex items-center gap-1.5">
                    <BadgeCheck className="h-4 w-4" /> REGISTERED ON TRATA ({registeredLabs.length})
                  </p>
                  {registeredLabs.map((lab) => (
                    <LabCard key={lab.id} lab={lab} selected={selectedLab?.id === lab.id} onSelect={pickLab} userId={user?._id} />
                  ))}
                </div>
              )}
              {filter !== "verified" && otherLabs.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-bold tracking-wide text-muted-foreground flex items-center gap-1.5">
                    <MapPin className="h-4 w-4" /> OTHER NEARBY LABS ({otherLabs.length})
                  </p>
                  {otherLabs.map((lab) => (
                    <LabCard key={lab.id} lab={lab} selected={selectedLab?.id === lab.id} onSelect={pickLab} userId={user?._id} />
                  ))}
                </div>
              )}
            </div>
          )}

          <LocationPickerDialog open={showLocationPicker} onOpenChange={setShowLocationPicker} />
        </>
      )}

      {/* Step: choose tests */}
      {step === "tests" && (
        <div className="space-y-4">
          <button className="text-sm text-primary font-medium" onClick={() => setStep("lab")} data-testid="lab-back-to-labs">← Change lab</button>
          <p className="font-semibold">{selectedLab?.displayName || selectedLab?.name}</p>
          {testsLoading ? (
            <div className="space-y-3">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-16 rounded-2xl" />)}</div>
          ) : (
            <div className="space-y-2">
              {rateCard.map((t) => {
                const on = !!selectedTests[t.testId];
                return (
                  <button
                    key={t.testId}
                    onClick={() => toggleTest(t)}
                    data-testid={`test-${t.testId}`}
                    className={`w-full text-left rounded-2xl border p-4 flex items-center gap-3 transition-all ${on ? "border-primary bg-accent/40" : "border-border hover:bg-accent/30"}`}
                  >
                    <div className={`h-5 w-5 rounded-md border flex items-center justify-center shrink-0 ${on ? "bg-primary border-primary" : "border-muted-foreground/40"}`}>
                      {on && <Check className="h-3.5 w-3.5 text-primary-foreground" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{t.testName}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {t.fastingRequired ? "Fasting required · " : ""}{t.reportTatHours ? `Report in ${t.reportTatHours}h` : ""}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      {t.discountedPrice && t.discountedPrice < t.price ? (
                        <>
                          <p className="text-sm font-semibold">{money(t.discountedPrice)}</p>
                          <p className="text-xs text-muted-foreground line-through">{money(t.price)}</p>
                        </>
                      ) : (
                        <p className="text-sm font-semibold">{money(t.price)}</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {chosen.length > 0 && (
            <div className="sticky bottom-24 md:bottom-4">
              <Button className="w-full rounded-2xl h-12" onClick={() => setStep("details")} data-testid="lab-continue-details">
                Continue · {chosen.length} test{chosen.length > 1 ? "s" : ""} · {money(subtotal)}
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Step: details + consent */}
      {step === "details" && (
        <div className="space-y-5">
          <button className="text-sm text-primary font-medium" onClick={() => setStep("tests")} data-testid="lab-back-to-tests">← Change tests</button>

          <Card className="rounded-3xl p-5 space-y-4">
            <div>
              <Label className="text-sm">Collection type</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                {[
                  { id: "home_collection", label: "Home collection", Icon: Home },
                  { id: "lab_visit", label: "Visit lab", Icon: Building2 },
                ].map((o) => (
                  <button
                    key={o.id}
                    onClick={() => setCollectionType(o.id)}
                    data-testid={`collection-${o.id}`}
                    className={`rounded-2xl border p-3 flex items-center gap-2 ${collectionType === o.id ? "border-primary bg-accent/40" : "border-border"}`}
                  >
                    <o.Icon className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">{o.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {collectionType === "home_collection" && (
              <div>
                <Label htmlFor="lab-address" className="text-sm">Collection address</Label>
                <Input id="lab-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="House / street / area" className="mt-1.5 rounded-2xl" data-testid="lab-address" />
              </div>
            )}

            <div>
              <Label htmlFor="lab-date" className="text-sm">Preferred date</Label>
              <Input id="lab-date" type="date" min={today} value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="mt-1.5 rounded-2xl" data-testid="lab-date" />
            </div>

            <div>
              <Label className="text-sm">Time slot</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {["06:00 - 08:00", "08:00 - 10:00", "10:00 - 12:00", "12:00 - 14:00", "16:00 - 18:00", "18:00 - 20:00"].map((slot) => (
                  <button
                    key={slot}
                    onClick={() => setTimeSlot(slot)}
                    data-testid={`slot-${slot.slice(0, 5)}`}
                    className={`rounded-2xl border py-2.5 text-xs font-medium transition-all ${timeSlot === slot ? "border-primary bg-accent/40 text-primary" : "border-border text-muted-foreground"}`}
                  >
                    {slot}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="lab-name" className="text-sm">Patient name</Label>
                <Input id="lab-name" value={patientName} onChange={(e) => setPatientName(e.target.value)} className="mt-1.5 rounded-2xl" data-testid="lab-patient-name" />
              </div>
              <div>
                <Label htmlFor="lab-phone" className="text-sm">Phone</Label>
                <Input id="lab-phone" value={patientPhone} onChange={(e) => setPatientPhone(e.target.value)} className="mt-1.5 rounded-2xl" data-testid="lab-patient-phone" />
              </div>
            </div>
          </Card>

          <Card className="rounded-3xl p-5">
            <p className="font-semibold text-sm mb-3">Order summary</p>
            <div className="space-y-2">
              {chosen.map((t) => (
                <div key={t.testId} className="flex justify-between text-sm">
                  <span className="text-muted-foreground truncate mr-2">{t.testName}</span>
                  <span>{money(t.discountedPrice || t.price)}</span>
                </div>
              ))}
              {homeFee > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Home collection fee</span>
                  <span>{money(homeFee)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold pt-2 border-t border-border">
                <span>Total</span>
                <span>{money(total)}</span>
              </div>
            </div>
          </Card>

          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer" data-testid="lab-digital-consent">
              <Checkbox checked={digitalConsent} onCheckedChange={(v) => setDigitalConsent(!!v)} className="mt-0.5" />
              <span className="text-sm text-muted-foreground">Save my report to Health Records automatically when ready.</span>
            </label>
            <label className="flex items-start gap-3 cursor-pointer" data-testid="lab-terms">
              <Checkbox checked={terms} onCheckedChange={(v) => setTerms(!!v)} className="mt-0.5" />
              <span className="text-sm text-muted-foreground">I accept the terms & consent to share my details with the selected lab.</span>
            </label>
          </div>

          <Button className="w-full rounded-2xl h-12" onClick={submit} disabled={submitting} data-testid="lab-confirm-booking">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            {submitting ? "Booking…" : `Confirm booking · ${money(total)}`}
          </Button>
        </div>
      )}

      {/* Step: done */}
      {step === "done" && booking && (
        <Card className="rounded-3xl p-8 text-center" data-testid="lab-booking-success">
          <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <p className="mt-4 text-lg font-semibold">Booking confirmed!</p>
          <p className="text-sm text-muted-foreground mt-1">Your booking number is</p>
          <p className="text-xl font-bold text-primary mt-1">{booking.bookingNumber}</p>
          <div className="mt-5 text-left rounded-2xl bg-accent/40 p-4 space-y-1 text-sm">
            <p><span className="text-muted-foreground">Lab:</span> {booking.labName}</p>
            <p><span className="text-muted-foreground">Date:</span> {booking.scheduledDate}</p>
            <p><span className="text-muted-foreground">Tests:</span> {(booking.tests || []).length}</p>
            <p><span className="text-muted-foreground">Total:</span> {money(booking.totalAmount)}</p>
          </div>
          <div className="flex gap-3 mt-6">
            <Button variant="outline" className="flex-1 rounded-2xl" onClick={() => navigate("/bookings-reports")} data-testid="lab-view-bookings">My bookings</Button>
            <Button className="flex-1 rounded-2xl" onClick={() => navigate("/home")} data-testid="lab-done-home">Done</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
